<script setup lang="ts">
import { useI18n } from '@/i18n';
import { Icon, LayoutGap, vTooltip } from '@dabble/quillui';
import { computed } from 'vue';

const props = defineProps<{ plan?: string | null }>();

const { t } = useI18n();

const featureList: { [feature: string]: string[] } = {
  all: ['free4eva', 'lifetime'],
  backup: ['basic', 'standard', 'premium'],
  chat_support: ['premium'],
  coauthoring: ['premium'],
  low_support: ['basic', 'standard', 'premium'],
  comments: ['basic', 'standard', 'premium'],
  darkmode: ['basic', 'standard', 'premium'],
  email_support: ['standard', 'premium'],
  everywhere: ['basic', 'standard', 'premium'],
  mobile: ['basic', 'standard', 'premium'],
  focus: ['standard', 'premium'],
  goals: ['basic', 'standard', 'premium'],
  grammar: ['premium'],
  images: ['standard', 'premium'],
  manuscript: ['basic', 'standard', 'premium'],
  plotting: ['standard', 'premium'],
  spelling: ['basic', 'standard', 'premium'],
  stickies: ['standard', 'premium'],
  story_notes: ['standard', 'premium'],
  characters: ['standard', 'premium'],
  sync: ['basic', 'standard', 'premium'],
  text_highlighting: ['basic', 'standard', 'premium'],
  unlimited_projects: ['basic', 'standard', 'premium'],
  versioning: [],
};

const applicableFeatures = computed(() => {
  if (!props.plan) return [];
  const normalizedPlanName = props.plan.toLowerCase().replace(/[^a-z0-9]/g, '');
  return Object.keys(featureList).filter((feature) => featureList[feature].includes(normalizedPlanName));
});
</script>

<template>
  <LayoutGap direction="column" size="xsmall" class="features-list">
    <LayoutGap
      v-for="feature in applicableFeatures"
      :key="feature"
      direction="row"
      vertical-align="center"
      size="xsmall"
      class="feature-item"
    >
      <Icon name="check" class="check-icon" />
      <span class="feature-name">{{ t(`features.feature_${feature}`) }}</span>
      <Icon v-tooltip="t(`features.feature_${feature}_description`)" name="info" class="info-icon" />
    </LayoutGap>
  </LayoutGap>
</template>

<style lang="scss" scoped>
.feature-item {
  line-height: 1.3;
  width: 100%;
}

.check-icon {
  color: var(--color-success, #10b981);
  flex-shrink: 0;
}

.info-icon {
  color: var(--text-color-lighter, #6b7280);
  flex-shrink: 0;
}

.feature-name {
  flex-grow: 1;
  min-width: 0;
  margin-left: 0.25rem;
  margin-right: 0.25rem;
}
</style>
